import React, { useState, useEffect, useRef } from "react";
import { Col, Button, Form, FormGroup, Input, Label, Spinner } from "reactstrap";
import JoditEditor from "jodit-react";
import { toast } from "react-toastify";
import { service } from "./service";
import { useLocation } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faPlus, faTimes, faSave } from "@fortawesome/free-solid-svg-icons";
import { CopyToClipboard } from "react-copy-to-clipboard";
import { service as blogService } from "../blog/service";
import HelpAccordion from "./HelpAccordion";
import "./help.css";

const Help = () => {
  const location = useLocation();
  const path = location.pathname.replace(/^\//, "").replace(/\//g, "_");
  const [searchTerm, setSearchTerm] = useState("");
  const [isShowForm, setIsShowForm] = useState(false);
  const [accordionItems, setAccordionItems] = useState([]);
  const [url, setUrl] = useState();
  const [copied, setCopied] = useState(false);
  const [loading, setLoading] = useState(true);
  const [errors, setErrors] = useState({});
  const [formData, setFormData] = useState({ question: "", htmlDescription: "", shortAnswer: "", isFromEdit: false, qs_id: "" });
  const editorRef = useRef(null);

  const isSuperAdmin = localStorage.getItem("superadmin") ? true : false;
  
  useEffect(() => {
    async function fetchData() {
      await fetchQuestions();
    }
    fetchData();
  }, [path]);

  // Observe editor content for inserted images and ensure a paragraph after each image
  useEffect(() => {
    if (!isShowForm) return; // only observe while form is visible

    let observer;

    const getEditorEditable = () => {
      try {
        // jodit-react may expose the underlying editor instance on ref.current
        const editor = editorRef && editorRef.current;
        // If the ref points to the Jodit instance, its editor element is editor.editor; otherwise try DOM query
        if (editor && editor.editor) return editor.editor;

        // fallback: find the first jodit-wysiwyg inside our editor-container
        const container = document.querySelector('.editor-container');
        if (!container) return null;
        return container.querySelector('.jodit-wysiwyg');
      } catch (e) {
        return null;
      }
    };

    const ensureParagraphAfter = (img) => {
      if (!img || !img.parentNode) return;
      // If image is wrapped inside a <p>, operate on that <p>
      const nodeToCheck = img.parentElement && img.parentElement.tagName === 'P' ? img.parentElement : img;
      const next = nodeToCheck.nextSibling;
      // If next sibling is already a paragraph, don't add
      if (next && next.nodeType === 1 && next.tagName === 'P') return;
      // Insert an empty paragraph with a <br>
      const p = document.createElement('p');
      p.appendChild(document.createElement('br'));
      if (nodeToCheck.parentNode) nodeToCheck.parentNode.insertBefore(p, nodeToCheck.nextSibling);
    };

    const handleMutations = (mutationsList) => {
      for (const m of mutationsList) {
        if (m.type === 'childList') {
          // check added nodes for images
          m.addedNodes.forEach((n) => {
            if (n.nodeType !== 1) return;
            if (n.tagName === 'IMG') {
              ensureParagraphAfter(n);
            } else {
              // find images inside the added node
              const imgs = n.querySelectorAll && n.querySelectorAll('img');
              if (imgs && imgs.length) imgs.forEach((i) => ensureParagraphAfter(i));
            }
          });
        }
      }
    };

    const editable = getEditorEditable();
    if (editable) {
      observer = new MutationObserver(handleMutations);
      observer.observe(editable, { childList: true, subtree: true });
    }

    return () => {
      if (observer) observer.disconnect();
    };
  }, [isShowForm, editorRef]);
  
  const fetchQuestions = async () => {
    try {
      setLoading(true);
      const response = await service.listQuestion(path);
      if (response && response.data) {
        const items = response.data.map((item) => ({
          title: item.question,
          content: item.short_answer,
          id: item.id,
          html_answer: item.answer_html,
          vanity_url: item.vanity_url,
        }));
        setAccordionItems(items);
      }
    } catch (error) {
      console.error("Error fetching questions:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleQuestionAdd = () => setIsShowForm(true);
  
  const handleInputChange = event => {
    const { name, value } = event.target;
    setFormData(prevFormData => ({ ...prevFormData, [name]: value }));
    setErrors(prevErrors => ({ ...prevErrors, [name]: "" }));
  };
  
  const handleEditorChange = (htmlDescription) => {
    setFormData((prevFormData) => ({ ...prevFormData, htmlDescription }));
    setErrors((prevErrors) => ({ ...prevErrors, htmlDescription: "" }));
  };

  const validateTextLines = (text) => ({
    valid: Math.ceil(text.length / 100) <= 3
  });

  const handleFormSubmit = async (event) => {
    event.preventDefault();
    let newErrors = {};
    if (!formData.question?.trim()) newErrors.question = "Please input the question";
    if (!formData.htmlDescription?.trim()) newErrors.htmlDescription = "Please input the answer.";
    if (!formData.shortAnswer?.trim()) newErrors.shortAnswer = "Please input the short answer.";
    if (!validateTextLines(formData.shortAnswer).valid) newErrors.shortAnswer = "Short answer cannot exceed 3 lines.";
    if (Object.keys(newErrors).length > 0) return setErrors(newErrors);

    let inputData = { question: formData.question, answer_html: formData.htmlDescription, short_answer: formData.shortAnswer, path: path };

    try {
      setLoading(true);
      const res = formData.isFromEdit
        ? await service.updateQuestion({ ...inputData, id: formData.qs_id })
        : await service.addQuestion(inputData);

      setIsShowForm(false);
      setFormData({ question: "", htmlDescription: "", shortAnswer: "", qs_id: "", isFromEdit: false });
      fetchQuestions();
    } catch (error) {
      console.error("Error adding question:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = (event) => {
    event.preventDefault();
    setIsShowForm(false);
    setErrors({});
    setFormData({ question: "", htmlDescription: "", shortAnswer: "", qs_id: "", isFromEdit: false });
  };

  const handleFileChange = (e) => {
    setUrl();
    const file = e.target.files[0];
    blogService.uploadImage({ image: file }).then((response) => {
      response.success ? setUrl(response.data[0].path) : toast.error("Something went wrong! Try again");
    });
  };

  const handleCopy = () => {
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div className="help-container">
      <div className="help-content">
        <div className="help-header">
          <div className="help-title animate-fade-in">
            {!isShowForm && <h1>Help Desk</h1>}
          </div>
          <div className="help-actions animate-slide-in">
            {isSuperAdmin && !isShowForm && (
              <Button
                color="primary"
                className="add-question-btn"
                onClick={handleQuestionAdd}
              >
                <FontAwesomeIcon icon={faPlus} />
                Question
              </Button>
            )}
            {!isShowForm && (
              <Button
                color="primary"
                className="help-close-btn"
                onClick={() =>
                  window.toggleHelpGlobal && window.toggleHelpGlobal()
                }
              >
                <FontAwesomeIcon icon={faTimes} />
                Close
              </Button>
            )}
          </div>
        </div>

        {loading ? (
          <div className="help-loader animate-pulse">
            <Spinner color="primary" />
          </div>
        ) : isShowForm ? (
          <div className="help-form-container animate-fade-in">
            <Form className="help-form">
              <div className="help-form-header">
                <div className="help-form-title">
                  <h2>Add Support Question</h2>
                </div>

                {url && (
                  <div className="file-upload-notification animate-fade-in">
                    {!copied && (
                      <div
                        className="file-upload-close"
                        onClick={() => {
                          setUrl();
                        }}
                      >
                        <FontAwesomeIcon icon={faTimes} />
                      </div>
                    )}
                    <div className="file-upload-success">
                      <p>File uploaded successfully!</p>
                      <CopyToClipboard text={url} onCopy={handleCopy}>
                        <Button
                          color="info"
                          size="sm"
                          className="copy-link-btn"
                        >
                          <span>Copy Link</span>
                        </Button>
                      </CopyToClipboard>
                      {copied && (
                        <p className="copied-message">Copied to clipboard!</p>
                      )}
                    </div>
                  </div>
                )}

                <div className="help-form-actions">
                  <Button
                    color="light"
                    className="help-cancel-btn btn btn-primary"
                    onClick={handleCancel}
                  >
                    <FontAwesomeIcon icon={faTimes} className="mr-2" />
                    Cancel
                  </Button>
                  <Button
                    color="primary"
                    className="help-submit-btn"
                    onClick={handleFormSubmit}
                  >
                    <FontAwesomeIcon icon={faSave} className="mr-2" />
                    Save
                  </Button>
                </div>
              </div>

              <div className="help-form-body">
                <FormGroup row className="form-group-question">
                  <Label for="question" sm={2}>
                    Question
                  </Label>
                  <Col sm={10}>
                    <Input
                      type="text"
                      name="question"
                      id="question"
                      value={formData.question}
                      onChange={handleInputChange}
                      placeholder="Enter your question here..."
                      className="question-input"
                    />
                    {errors.question && (
                      <p className="field-error">{errors.question}</p>
                    )}
                  </Col>
                </FormGroup>

                <FormGroup row className="form-group-file">
                  <Label for="image" sm={2}>
                    Attach File
                  </Label>
                  <Col sm={10}>
                    <div className="file-upload-container">
                      <input
                        type="file"
                        id="image"
                        name="image"
                        onChange={handleFileChange}
                        className="file-input"
                      />
                    </div>
                  </Col>
                </FormGroup>

                <FormGroup row className="form-group-answer">
                  <Label for="htmlDescription" sm={2}>
                    Answer
                  </Label>
                  <Col sm={10}>
                    <div className="editor-container">
                          <JoditEditor
                            ref={editorRef}
                            tabIndex={1}
                            value={formData.htmlDescription}
                            onBlur={handleEditorChange}
                            onChange={handleEditorChange}
                          />
                    </div>
                    {errors.htmlDescription && (
                      <p className="field-error">{errors.htmlDescription}</p>
                    )}
                  </Col>
                </FormGroup>

                <FormGroup row className="form-group-short-answer">
                  <Label for="shortAnswer" sm={2}>
                    Short Answer
                  </Label>
                  <Col sm={10}>
                    <Input
                      type="textarea"
                      name="shortAnswer"
                      id="shortAnswer"
                      value={formData.shortAnswer}
                      onChange={handleInputChange}
                      placeholder="Enter a concise summary (max 3 lines)..."
                      className="shortanswer-input"
                    />
                    {errors.shortAnswer && (
                      <p className="field-error">{errors.shortAnswer}</p>
                    )}
                  </Col>
                </FormGroup>
              </div>
            </Form>
          </div>
        ) : accordionItems.length > 0 ? (
          <div className="help-content-container">
            <HelpAccordion
              items={accordionItems}
              searchTerm={searchTerm}
              setAccordionItems={setAccordionItems}
              setIsShowForm={setIsShowForm}
              setFormData={setFormData}
              setLoading={setLoading}
              isSuperAdmin={isSuperAdmin}
            />
          </div>
        ) : (
          <div className="no-faq-container animate-fade-in">
            <div className="no-faq-icon">❔</div>
            <h3>No FAQs Available</h3>
            <p>Help content for this section will be available soon!</p>
            {isSuperAdmin && (
              <Button
                color="primary"
                className="add-first-question-btn"
                onClick={handleQuestionAdd}
              >
                <FontAwesomeIcon icon={faPlus} className="mr-2" />
                First Question
              </Button>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default Help;
